// Vipps MobilePay Widget SDK: den godkjente «Betal med Vipps»-knappen på betalingssiden (Vipps'
// sjekkliste: bruk offisielle knapper, ikke egne). Dokumentasjon:
// https://developer.vippsmobilepay.com/docs/buttons-and-widgets/widget/
//
// - Skriptet hentes fra Vipps' CDN og endres uten varsel: aldri SRI-hash eller hash i CSP.
// - På PC åpner knappen betalingen i en dialog (host + iframe fra pay.vipps.no); på mobil og nettbrett
//   går siden rett til Vipps. Etter godkjent eller avbrutt sender SDK-en kunden til retur-adressen
//   (/c/:token/retur), som tåler å bli åpnet to ganger samtidig (iframe + side).
// - Pay har ingen samtykkebanner, så samtykkestyrte funksjoner (gjenkjenning og bruksstatistikk) slås av.

export interface VippsKnapp {
  brand(v: 'vipps' | 'mobilepay'): VippsKnapp;
  language(v: 'no' | 'en' | 'da' | 'fi' | 'sv'): VippsKnapp;
  verb(v: 'pay' | 'continue' | 'confirm' | 'buy'): VippsKnapp;
  stretched(v: boolean): VippsKnapp;
  mount(selector: string): VippsKnapp;
  unmount(): void;
}

interface VippsTrigger {
  button(): VippsKnapp;
}

interface VippsSdk {
  host(): { start(): void; stop(): void };
  trigger(resolver: () => Promise<string>, opts?: { cancelPaymentOnClose?: boolean }): VippsTrigger;
  consent?(choices: { rememberMe?: boolean; analytics?: boolean }): void;
}

declare global {
  interface Window {
    vipps?: VippsSdk;
  }
}

const SKRIPT = 'https://cdn.vippsmobilepay.com/js/widget-sdk/vipps-widget.js';
const TIDSAVBRUDD_MS = 8000;

let lasting: Promise<VippsSdk> | null = null;
let hostStartet = false;

/** Laster SDK-en én gang per side. Avvises ved nettverksfeil, blokkering eller tidsavbrudd. */
export function lastVippsSdk(): Promise<VippsSdk> {
  if (window.vipps) return Promise.resolve(oppsett(window.vipps));
  if (lasting) return lasting;
  lasting = new Promise<VippsSdk>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SKRIPT;
    s.async = true;
    s.setAttribute('data-vipps-widget-sdk', '');
    const tid = window.setTimeout(() => reject(new Error('Vipps-knappen lastet ikke')), TIDSAVBRUDD_MS);
    s.onload = () => {
      window.clearTimeout(tid);
      if (window.vipps) resolve(oppsett(window.vipps));
      else reject(new Error('Vipps-knappen lastet ikke'));
    };
    s.onerror = () => {
      window.clearTimeout(tid);
      reject(new Error('Vipps-knappen lastet ikke'));
    };
    document.head.appendChild(s);
  }).catch((err: unknown) => {
    lasting = null; // neste visning av siden kan prøve igjen
    throw err;
  });
  return lasting;
}

function oppsett(sdk: VippsSdk): VippsSdk {
  if (!hostStartet) {
    sdk.consent?.({ rememberMe: false, analytics: false });
    sdk.host().start();
    hostStartet = true;
  }
  return sdk;
}

/**
 * Monterer knappen i elementet `selector`. `startBetaling` oppretter betalingen hos Vipps og gir
 * redirect-adressen (pay.vipps.no/…); kaster den, åpnes ingenting.
 */
export function monterVippsKnapp(sdk: VippsSdk, selector: string, startBetaling: () => Promise<string>): VippsKnapp {
  return sdk.trigger(startBetaling).button().brand('vipps').language('no').verb('pay').stretched(true).mount(selector);
}
